import React, { useEffect, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom';
import { DeviceDetails } from '@dev/ee-modules';
import { ToggleButtonGroup, DesktopIcon, PhoneIcon, TabletIcon, OtherDevicesIcon } from '@dev/ee-components';
import styled from 'styled-components';
import { isEmpty, truncate } from 'lodash-es';
import { getDeviceDetailsStore, DevicesFetchStart, DevicesChannelsFetchStart } from '@api/store/deviceDetails/actions';
import { LoadChannelsStart } from '@api/store/channels/actions';
import { DevicesChannelParams } from '@api/services/ElasticApi';
import dayjs from '@api/services/tools/dayjs';
import { LONG_DATE_FORMAT, MAX_CHANNELS_LIST_LENGTH, MAX_CHANNELS_LIST_WITH_QUERY_LENGTH } from '@api/services/constants';
import { DateDropdownOptions } from '@api/store/suppressions/types';
import { AccessLevel } from '@dev/ee-api/ee-api-models';

import {
    DeviceToggleBtnValues,
    DeviceDetailsToggleBtnOptInterface,
    LegendDataType,
    ToggleButtonProps,
    RadioValues,
} from './DeviceDetailsInterface';
import { usePieChartsData } from './hooks/usePieChartsData';
import { createRows, createMobileRows } from './hooks/useTableRows';
import { useMobile } from '@api/services/hooks/useMobile';
import Filters from './Filters';
import { getAccessLevels } from '@api/store/account/actions';

const DeviceDetailsOverviewStyled = styled.div`
    padding-top: 26px;
`;

const ToggleButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    background-color: inherit;
    margin-bottom: 24px;
`;

const useLegendData = (): LegendDataType => {
    const { t } = useTranslation();

    return [
        {
            name: t('desktop'),
            icon: <DesktopIcon />,
        },
        {
            name: t('mobile'),
            icon: <PhoneIcon />,
        },
        {
            name: t('tablet'),
            icon: <TabletIcon />,
        },
        {
            name: t('other'),
            icon: <OtherDevicesIcon />,
        },
    ];
};

const toggleBtnOptions: DeviceDetailsToggleBtnOptInterface[] = [
    {
        label: '#',
        value: DeviceToggleBtnValues.Number,
    },
    {
        label: '%',
        value: DeviceToggleBtnValues.Percent,
    },
];

const ToggleButton: FC<ToggleButtonProps> = (props: ToggleButtonProps): JSX.Element => {
    const { setToggleBtnValue } = props;
    return (
        <ToggleButtonWrapper>
            <ToggleButtonGroup
                defaultValue={DeviceToggleBtnValues.Number}
                size="small"
                options={toggleBtnOptions}
                onChange={(format) => setToggleBtnValue(format as DeviceToggleBtnValues)}
            />
        </ToggleButtonWrapper>
    );
};

const DeviceDetailsOverview = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const location = useLocation();
    const { deviceDetailsStats, loading } = useSelector(getDeviceDetailsStore);
    const accessLevels = useSelector(getAccessLevels);

    const query = parse(location.search);
    const paramTo = (query?.to as string) || dayjs().format(LONG_DATE_FORMAT);
    const paramFrom =
        (query?.from as string) || dayjs().subtract(parseInt(DateDropdownOptions.Last3months, 10), 'day').format(LONG_DATE_FORMAT);
    const paramChannelName = (query?.channelName as string) || null;

    const [toggleBtnValue, setToggleBtnValue] = useState<DeviceToggleBtnValues>(DeviceToggleBtnValues.Number);
    const [from, setFrom] = useState(paramFrom);
    const [to, setTo] = useState(paramTo);
    const [queryChannelName, setQueryChannelName] = useState<string | undefined>(paramChannelName);
    const [selectedChannelName, setSelectedChannelName] = useState<string | undefined>(paramChannelName);
    const [radioValue, setRadioValue] = useState(RadioValues.Opened);
    const hasViewChannelsAL = accessLevels?.includes(AccessLevel.ViewChannels);

    useEffect(() => {
        if (hasViewChannelsAL) {
            dispatch(
                LoadChannelsStart({
                    search: queryChannelName ?? '',
                    limit: queryChannelName ? MAX_CHANNELS_LIST_WITH_QUERY_LENGTH : MAX_CHANNELS_LIST_LENGTH,
                })
            );
        }
    }, [queryChannelName]);

    useEffect(() => {
        const requestParams = {
            from,
            to,
            channelName: selectedChannelName,
        };
        if (requestParams.channelName) {
            dispatch(DevicesChannelsFetchStart(requestParams as DevicesChannelParams));
        } else {
            dispatch(DevicesFetchStart(requestParams));
        }
    }, [from, to, selectedChannelName]);

    const detailedTableData = createRows(deviceDetailsStats, toggleBtnValue);
    const mobileDetailedTableData = createMobileRows(deviceDetailsStats, toggleBtnValue, radioValue);
    const chartSize = { width: 320, height: 400, outerRadius: 150, innerRadius: 115 };
    const chartsData = usePieChartsData();
    const { pieChartDataOpened, pieChartDataClicked, pieChartDataUnsubscribed } = chartsData;

    return (
        <DeviceDetailsOverviewStyled>
            <DeviceDetails
                filters={
                    <Filters
                        from={from}
                        to={to}
                        setFrom={setFrom}
                        setTo={setTo}
                        queryChannelName={queryChannelName}
                        setQueryChannelName={setQueryChannelName}
                        selectedChannelName={selectedChannelName}
                        setSelectedChannelName={setSelectedChannelName}
                        hasViewChannelsAL={hasViewChannelsAL}
                    />
                }
                pieChartDataOpened={pieChartDataOpened}
                pieChartDataOpenedTitle={t('opened')}
                isEmptyChartOpened={isEmpty(pieChartDataOpened)}
                pieChartDataClicked={pieChartDataClicked}
                pieChartDataClickedTitle={t('clicked')}
                isEmptyChartClicked={isEmpty(pieChartDataClicked)}
                pieChartDataUnsubscribed={pieChartDataUnsubscribed}
                pieChartDataUnsubscribedTitle={t('unsubscribed')}
                isEmptyChartUnsubscribed={isEmpty(pieChartDataUnsubscribed)}
                chartSize={chartSize}
                tableRows={detailedTableData}
                mobileTableRows={mobileDetailedTableData}
                loading={loading}
                toggleButton={<ToggleButton setToggleBtnValue={setToggleBtnValue} />}
                legendData={useLegendData()}
                isMobile={isMobile}
                radioValue={radioValue}
                campaignName={!selectedChannelName ? t('allchannels') : truncate(selectedChannelName, { length: 60 })}
                handleRadioChange={(ev): void => {
                    setRadioValue(ev?.target?.value || RadioValues.Opened);
                }}
            />
        </DeviceDetailsOverviewStyled>
    );
};

export default DeviceDetailsOverview;
